import React, { ReactElement, useEffect, useRef, useState } from "react";

import axios from "axios";

import { ConnectedProps, connect } from "react-redux";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import { RootState } from "App/Store";

import useInterval from "Common/useInterval";

import { getSessionTimeout, keepAlive, sessionTimedOut } from "Adapters/profileAdapter";

import { getTimeout, invalidateTimeout, updateTimeout } from "Features/Security";

const mapStateToProps = (state: RootState) => ({
  invalidAntiforgery: state.profile.antiforgeryError,
});

const connector = connect(mapStateToProps);
type TypesFromRedux = ConnectedProps<typeof connector>;

export function PageTimeout(props: TypesFromRedux): ReactElement | null {
  const [timeRemaining, setTimeRemaining] = useState<number>();
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [isExtending, setIsExtending] = useState<boolean>(false);
  const extending = useRef<boolean>(false);
  const timeoutVersion = useRef<number>(0);
  const mounted = useRef<boolean>(true);
  const nextPollAt = useRef<number>(0);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  function showTimeoutState(): void {
    if (!mounted.current) {
      return;
    }

    const timeout: number | null = getTimeout();
    if (timeout !== null && timeout <= 0) {
      void sessionTimedOut();
    } else if (timeout !== null && timeout < 60000) {
      setTimeRemaining(Math.max(0, Math.floor(timeout / 1000)));
      setDialogVisible(true);
    } else {
      setDialogVisible(false);
    }
  }

  useInterval(async () => {
    if (extending.current || !mounted.current) {
      return;
    }
    const version = timeoutVersion.current;
    const cachedTimeout = getTimeout();
    if (Date.now() < nextPollAt.current && (cachedTimeout === null || cachedTimeout > 0)) {
      showTimeoutState();
      return;
    }

    // Keep the countdown responsive without making a request on every tick.
    nextPollAt.current = Date.now() + 30000;
    try {
      const remainingMilliseconds: number = await getSessionTimeout();
      if (!mounted.current || version !== timeoutVersion.current) {
        return;
      }

      updateTimeout(remainingMilliseconds);
      nextPollAt.current = Date.now() + (remainingMilliseconds < 90000 ? 5000 : 30000);
    } catch (error: unknown) {
      if (!mounted.current || version !== timeoutVersion.current) {
        return;
      }
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        if (mounted.current) {
          await sessionTimedOut();
        }
        return;
      }
    }

    showTimeoutState();
  }, 1000);

  function handleCloseDialog() {
    setDialogVisible(false);
  }

  async function handleExtendSession() {
    if (extending.current) {
      return;
    }
    extending.current = true;
    setIsExtending(true);
    // Polls started before Continue cannot describe the renewed session.
    timeoutVersion.current += 1;
    try {
      await keepAlive();
      if (!mounted.current) {
        return;
      }
      // Renewal succeeded, so the old expiry is no longer authoritative.
      // Leave the deadline unknown until the server supplies a fresh lifetime.
      invalidateTimeout();
      const remainingMilliseconds: number = await getSessionTimeout();
      if (!mounted.current) {
        return;
      }

      updateTimeout(remainingMilliseconds);
      nextPollAt.current = Date.now() + (remainingMilliseconds < 90000 ? 5000 : 30000);
      showTimeoutState();
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 401 && mounted.current) {
        await sessionTimedOut();
      } else if (mounted.current) {
        setDialogVisible(false);
        nextPollAt.current = 0;
      }
    } finally {
      extending.current = false;
      if (mounted.current) {
        setIsExtending(false);
      }
    }
  }

  if (props.invalidAntiforgery) {
    return null;
  }

  return (
    <Dialog maxWidth="xs" id="modal-timeout" onClose={handleCloseDialog} open={dialogVisible}>
      <DialogTitle id="modal-timeout-title">Continue session?</DialogTitle>
      <DialogContent id="modal-timeout-content">
        Your session is set to expire in {timeRemaining} seconds. Click continue to extend your session.
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" size="large" disabled={isExtending} onClick={handleExtendSession}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default connector(PageTimeout);
